/**
 * F31 (Wave 2B) — admin skill-dispute detail page.
 *
 * Route: `/skill-disputes/:id`
 * Permission: `skill_disputes:resolve` (super_admin + support — same as the
 * queue; the page is route-gated, so the Resolve CTA is always available to a
 * viewer who can reach it).
 *
 * Shows the full match context (sport / reported result / score) + the dispute
 * reason + participant testimony, with the ResolveSkillDisputeDialog trigger.
 * Resolving posts to `POST /admin/skill-disputes/:id/resolve` which applies the
 * final ELO delta server-side + writes the admin audit row.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  useToast,
  Avatar,
} from '@bravemove/ui-web';
import { SPORT_SUPPORTS_DRAWS } from '@bravemove/types';
import type {
  MatchOutcomeDetail,
  MatchOutcomeStatus,
  ResolveMatchDisputeInput,
} from '@bravemove/types';
import { adminSkillDisputesApi } from '../../api/skillDisputes';
import { ResolveSkillDisputeDialog } from '../../components/skill/ResolveSkillDisputeDialog';
import { translateApiError } from '../../auth/errors';

const STATUS_CHIP_MAP: Record<MatchOutcomeStatus, StatusChipStatus> = {
  pending_confirmation: 'pending',
  confirmed: 'approved',
  disputed: 'in_review',
  resolved_by_admin: 'approved',
  expired: 'rejected',
};

export function AdminSkillDisputeDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  const [outcome, setOutcome] = useState<MatchOutcomeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const d = await adminSkillDisputesApi.disputeDetail(id);
      if (d === null) setNotFound(true);
      else setOutcome(d);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const handleResolve = async (input: ResolveMatchDisputeInput): Promise<void> => {
    if (!id) return;
    setResolveError(null);
    try {
      const updated = await adminSkillDisputesApi.resolveDispute(id, input);
      setOutcome(updated);
      setResolveOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:skill.disputes.detail.title', { defaultValue: 'Dispute resolved' }),
      });
    } catch (err) {
      setResolveError(translateApiError(err, t));
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="skill-dispute-detail-loading">
        <Skeleton className="h-7 w-1/3 rounded-md" />
        <Skeleton className="h-28 w-full rounded-lg" />
      </div>
    );
  }

  if (notFound) {
    return (
      <EmptyState
        title={t('admin:skill.disputes.detail.not_found_title', {
          defaultValue: 'Dispute not found',
        })}
        description={t('admin:skill.disputes.detail.not_found_body', {
          defaultValue: "This dispute doesn't exist.",
        })}
      />
    );
  }

  if (error) {
    return <Banner tone="danger">{error}</Banner>;
  }

  if (!outcome) return null;

  const supportsDraws = SPORT_SUPPORTS_DRAWS[outcome.sport] === true;
  const canResolve = outcome.status === 'disputed';

  return (
    <div className="space-y-6" data-testid="skill-dispute-detail-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-ink capitalize">
            {outcome.sport.replace(/-/g, ' ')}
          </h1>
          <p className="text-[12px] text-ink-3 mt-0.5 font-mono">{outcome.bookingId}</p>
        </div>
        <StatusChip
          status={STATUS_CHIP_MAP[outcome.status]}
          label={t(`skill:outcome.status.${outcome.status}`)}
        />
      </div>

      {/* Side-by-side submission panels: captain's report vs challenger's dispute */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Left: captain's report */}
        <Card>
          <div className="flex flex-col gap-3">
            <h2 className="text-[13px] font-semibold text-ink-2 uppercase tracking-wide">
              {t('admin:skill.disputes.detail.report_heading', {
                defaultValue: "Captain's report",
              })}
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold text-ink">
                {outcome.winningSide === 'draw'
                  ? t('skill:outcome.result.draw', { defaultValue: 'Draw' })
                  : outcome.winningSide
                    ? `${t('skill:outcome.result.win', { defaultValue: 'Won' })}: ${t(
                        `admin:skill.disputes.detail.side_${outcome.winningSide}`,
                        { defaultValue: `Side ${outcome.winningSide.toUpperCase()}` },
                      )}`
                    : '—'}
              </span>
              {outcome.scoreA !== null && outcome.scoreB !== null ? (
                <span className="font-mono text-[13px] text-ink-3">
                  {outcome.scoreA}–{outcome.scoreB}
                </span>
              ) : null}
            </div>
            {outcome.participants.length > 0 ? (
              <ul className="flex flex-col gap-1.5" role="list" data-testid="dispute-participants">
                {outcome.participants.map((p) => (
                  <li key={p.userId} className="flex items-center gap-2 text-[12.5px]">
                    <Avatar name={p.userId} size="xs" />
                    <span className="font-mono text-ink-2">{p.userId.slice(0, 8)}</span>
                    <span className="ms-auto text-ink-3">{p.role}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </Card>

        {/* Right: challenger's dispute */}
        <Card>
          <div className="flex flex-col gap-3">
            <h2 className="text-[13px] font-semibold text-ink-2 uppercase tracking-wide">
              {t('admin:skill.disputes.detail.dispute_heading', { defaultValue: 'Dispute' })}
            </h2>
            <p className="text-[13px] text-ink" data-testid="dispute-reason-text">
              {outcome.disputeReason ?? '—'}
            </p>
            {outcome.disputedByUserId ? (
              <div className="flex items-center gap-2 mt-1">
                <Avatar name={outcome.disputedByUserId} size="xs" />
                <span className="font-mono text-[11.5px] text-ink-3">
                  {outcome.disputedByUserId.slice(0, 8)}
                </span>
              </div>
            ) : null}
          </div>
        </Card>
      </div>

      {canResolve ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setResolveOpen(true)} data-testid="open-resolve">
            {t('admin:skill.disputes.detail.resolve_cta', { defaultValue: 'Resolve dispute' })}
          </Button>
        </div>
      ) : null}

      <button
        type="button"
        className="text-[12px] text-accent hover:underline"
        onClick={() => navigate('/skill-disputes')}
      >
        {t('admin:skill.disputes.detail.back', { defaultValue: 'Back to disputes' })}
      </button>

      <ResolveSkillDisputeDialog
        open={resolveOpen}
        supportsDraws={supportsDraws}
        onResolve={handleResolve}
        onClose={() => setResolveOpen(false)}
        errorMessage={resolveError}
      />
    </div>
  );
}

export default AdminSkillDisputeDetailPage;
